import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  directoryApi,
  type ReplaceSiteMembersRequest,
  type ReplaceUserSitesRequest,
  type SiteWithMembers,
  type UserSite,
} from '../client';

/**
 * Stable query-key factories for the user-site / site-with-members
 * caches. Two namespaces because the read paths are distinct
 * (per-user vs per-org) but they invalidate each other on writes —
 * adding a user to a site changes both views.
 */
export const userSiteKeys = {
  all: ['directory', 'user-sites'] as const,
  byUser: (userId: string) => ['directory', 'user-sites', 'user', userId] as const,
};

export const siteWithMembersKeys = {
  all: ['directory', 'sites-with-members'] as const,
  byOrg: (organisationId: string) =>
    ['directory', 'sites-with-members', 'org', organisationId] as const,
};

/**
 * Fetch the sites a single user is currently assigned to. Used by the
 * Users editor to pre-fill the Sites multi-select.
 */
export function useUserSites(
  userId: string | null,
  options?: Omit<UseQueryOptions<UserSite[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<UserSite[], Error>({
    queryKey: userId ? userSiteKeys.byUser(userId) : userSiteKeys.all,
    queryFn: () => {
      if (!userId) throw new Error('No user id');
      return directoryApi.getUserSites(userId);
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Replace the full Site assignment list for a user. Empty list = clear
 * all. Invalidates BOTH the per-user cache and any org-level
 * sites-with-members cache that might be showing the same data from
 * the other direction.
 */
export function useReplaceUserSites() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { userId: string; organisationId?: string; body: ReplaceUserSitesRequest }
  >({
    mutationKey: [...userSiteKeys.all, 'replace-user'],
    mutationFn: ({ userId, body }) => directoryApi.replaceUserSites(userId, body),
    onSuccess: (_res, { userId, organisationId }) => {
      qc.invalidateQueries({ queryKey: userSiteKeys.byUser(userId) });
      if (organisationId) {
        qc.invalidateQueries({ queryKey: siteWithMembersKeys.byOrg(organisationId) });
      } else {
        // Caller didn't pass org context; nuke the whole namespace
        // rather than leave stale data.
        qc.invalidateQueries({ queryKey: siteWithMembersKeys.all });
      }
    },
  });
}

/**
 * Fetch sites with embedded members for the Sites tab. Single
 * round-trip for grid + drawer payloads.
 */
export function useSitesWithMembers(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<SiteWithMembers[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<SiteWithMembers[], Error>({
    queryKey: organisationId
      ? siteWithMembersKeys.byOrg(organisationId)
      : siteWithMembersKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getSitesWithMembers(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Replace the full member list for a single site. Sites-direction
 * mirror of `useReplaceUserSites`. Invalidates both the org-level
 * sites view and any per-user caches that might overlap.
 */
export function useReplaceSiteMembers() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { siteId: string; organisationId: string; body: ReplaceSiteMembersRequest }
  >({
    mutationKey: [...siteWithMembersKeys.all, 'replace-members'],
    mutationFn: ({ siteId, body }) => directoryApi.replaceSiteMembers(siteId, body),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: siteWithMembersKeys.byOrg(organisationId) });
      // Per-user caches across the affected users could be stale; the
      // mutation doesn't know which users were touched without diffing,
      // so nuke the whole namespace. Cheap — it's just a few small lists.
      qc.invalidateQueries({ queryKey: userSiteKeys.all });
    },
  });
}
